import {
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { ROUTES } from "domain/configuration";
import {
  AddressType,
  BillEstimate,
  EligibilityType,
  MapProductNameToVehicleType,
  ProductName,
} from "domain/core/types";
import { RequestStatus } from "lib/types";
import {
  formatAddress,
  reportEligibility,
  sendAnalyticsPageView,
} from "lib/util";
import React, { ChangeEvent, MouseEvent, useCallback, useMemo } from "react";
import { styled } from "styled-components";
import Button from "ui/components/Button";
import {
  ContentWrapper,
  RowLayout,
  RowLayoutLeft,
  RowLayoutRight,
} from "ui/components/StyledComponents";
import { COLORS, PADDING } from "ui/theme";

const AddressHeader = styled.h3`
  color: ${COLORS.charcoal};
  font-size: ${20 / 16}rem;
  font-weight: 400;
  padding-top: ${PADDING.small};
  padding-bottom: ${PADDING.medium};
  margin: 0;
`;

const SCircularProgress = styled(CircularProgress)`
  margin: 1rem;
`;

const LabelButton = styled.p`
  cursor: pointer;
  border: 1px solid ${COLORS.lightGrey};
  border-radius: 12px;
  padding: ${PADDING.small} ${PADDING.xLarge};
  background: ${COLORS.white};
  text-transform: capitalize;

  &[data-checked="true"] {
    background: ${COLORS.mist};
    border-color: ${COLORS.indigo};
    color: ${COLORS.indigo};
  }
`;

const HiddenRadio = styled(Radio)`
  height: 1px;
  overflow: hidden;
  padding: 0px;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const SRadioGroup = styled(RadioGroup)`
  flex-direction: row;
`;

const SFormControlLabel = styled(FormControlLabel)`
  margin-left: 0;
`;

interface ConfirmationProps {
  address: AddressType;
  eligibility?: EligibilityType;
  onCheckAnother(): void;
  onEnterManualICP(): void;
  onJoinNowClicked(payload: MouseEvent): void;
  onVehicleTypeClicked(payload: ChangeEvent): void;
  billEstimate?: BillEstimate;
  billEstimates?: BillEstimate[];
  fetchStatus: RequestStatus;
  selectedProduct: string;
  selectedProductError: string;
  firstFormInteraction: boolean;
  setFirstFormInteraction: (value: boolean) => void;
}

const isEligible = (val: string | undefined): boolean => {
  switch (val) {
    case "upgrade":
    case "not_in_area":
    case "reject":
    case "reject_commercial":
    case "export":
    case typeof undefined:
      return false;
    default:
      return true;
  }
};

export default function AddressConfirmation(props: ConfirmationProps) {
  useMemo(() => {
    if (!props.firstFormInteraction) return;
    reportEligibility(props.eligibility);
    props.setFirstFormInteraction(false);
  }, [props.firstFormInteraction]);

  const handleBackToZElec = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      event.preventDefault();
      window.open("/", "_self");
    },
    [],
  );

  const getResultHeaderCopy = useCallback(() => {
    let headerCopy = "";
    switch (props.eligibility) {
      case "success":
      case "manual":
        headerCopy = "Great, you can get power at";
        break;
      case "upgrade":
      case "export":
      case "not_in_area":
      case "reject":
        headerCopy =
          "Based on the information about your address we can’t connect with you.";
        break;
      case "reject_commercial":
        headerCopy =
          "This address appears to be a commercial property, which means we aren't able to connect to it";
        break;
      default:
        headerCopy = "We can't find your place right now";
        break;
    }
    sendAnalyticsPageView(headerCopy);
    return headerCopy;
  }, [props.eligibility]);

  const renderVehicleTypeSelect = useCallback(() => {
    const availableProducts = props.billEstimate
      ? props.billEstimates.map((estimate) => estimate.product_name)
      : Object.values(ProductName);

    const route =
      props.selectedProduct === ProductName.Z_ELECTRIC_REWARDS &&
      (props.billEstimates?.length === 0 || props.billEstimates === undefined)
        ? ROUTES.details
        : ROUTES.planDetails;

    return (
      <>
        <Typography variant="h3">What type of vehicle do you drive?</Typography>
        <Typography variant="body1">
          We need to know what kind of vehicle you drive as our power plans are
          designed around your vehicle.
        </Typography>
        <SRadioGroup
          name="productSelection"
          value={props.selectedProduct}
          onChange={props.onVehicleTypeClicked}
        >
          {availableProducts.map((product) => (
            <div key={`radio-product-option-${product}`}>
              <SFormControlLabel
                id={`radio-product-option-${product}`}
                value={product}
                checked={props.selectedProduct === product}
                control={<HiddenRadio />}
                label={
                  <LabelButton data-checked={props.selectedProduct === product}>
                    {MapProductNameToVehicleType[product]}
                  </LabelButton>
                }
              />
            </div>
          ))}
        </SRadioGroup>
        <Typography variant="caption" display="inline" color="error">
          {props.selectedProductError}
        </Typography>
        {props.fetchStatus === "Fulfilled" && (
          <Button
            onClick={props.onJoinNowClicked}
            variant="contained"
            id="button-confirmed-next"
            component={Link}
            to={route}
          >
            Continue
          </Button>
        )}
      </>
    );
  }, [
    props.billEstimates,
    props.selectedProduct,
    props.fetchStatus,
    props.selectedProductError,
  ]);

  const getResultSubHeader = useCallback(() => {
    const defaultReturn = (
      <Typography variant="body2">
        Sorry! We can't give you a price estimate because we can't connect to
        the power meter at that address.
      </Typography>
    );

    switch (props.eligibility) {
      case "success":
      case "manual":
        //We only want to say that the bill estimate cannot be calculated if they are eligible to sign up with Z Electric
        if (props.fetchStatus === "Failed") {
          return defaultReturn;
        }

        return renderVehicleTypeSelect();
      case "upgrade":
        return (
          <Typography variant="body1">
            Unfortunately, it looks like you don't have a smart meter at this
            address, so we can't connect. We suggest you find a retailer that is
            able to do manual meter reads.
          </Typography>
        );
      case "not_in_area":
        return (
          <Typography variant="body1">
            Unfortunately we're not currently operating in your network area.
          </Typography>
        );
      case "reject_commercial":
        return (
          <Typography variant="body1">
            If you think that this isn't correct, and your address isn't a
            commercial property, please email helpwithpower@z.co.nz or give us a
            call on 0800 935 328.
          </Typography>
        );
      case "reject":
      case "export":
        return (
          <Typography variant="body1">
            Unfortunately, we currently don't support the meter setup at this
            address.
          </Typography>
        );
      default:
        return defaultReturn;
    }
  }, [
    props.eligibility,
    props.fetchStatus,
    props.billEstimate,
    props.selectedProductError,
  ]);

  const renderResult = useCallback(() => {
    // Not eligibile for now
    if (!isEligible(props.eligibility)) {
      return (
        <RowLayout>
          <Button
            variant="contained"
            id="back-to-z-homepage"
            onClick={handleBackToZElec}
          >
            Back to homepage
          </Button>
          <Link
            component="button"
            variant="body1"
            id="button-check-another"
            onClick={props.onCheckAnother}
            sx={{ marginTop: PADDING.medium }}
          >
            Check another address
          </Link>
        </RowLayout>
      );
    }

    if (props.fetchStatus === "Pending") {
      return <SCircularProgress />;
    }

    // Not sure if eligible, continue filling out form, show fuel rewards
    if (!Boolean(props.fetchStatus === "Fulfilled")) {
      return (
        <>
          <Typography variant="body1">
            If you have a recent power bill or if you can get to the power
            meter, simply enter your ICP manually below to get back on track.
          </Typography>
          <Typography variant="body1">
            Or, you can continue with your application, and we'll get in touch
            after you submit to gather more information and let you know if we
            can supply to your address.
          </Typography>
          {renderVehicleTypeSelect()}
          <RowLayout>
            <RowLayoutLeft>
              <Button
                onClick={props.onJoinNowClicked}
                variant="contained"
                id="button-confirmed-next"
                component={Link}
                to={ROUTES.planDetails}
              >
                Continue
              </Button>
            </RowLayoutLeft>
            <RowLayoutRight>
              <Link
                onClick={props.onEnterManualICP}
                component="button"
                sx={{ paddingTop: PADDING.medium }}
              >
                Enter ICP manually
              </Link>
            </RowLayoutRight>
          </RowLayout>
        </>
      );
    }
  }, [props.fetchStatus, props.eligibility, props.selectedProduct]);

  return (
    <>
      <Container component="main">
        <ContentWrapper>
          <Typography variant="h2">{getResultHeaderCopy()}</Typography>
          {isEligible(props.eligibility) && (
            <RowLayout>
              <AddressHeader id="text-confirmed-address">
                {formatAddress({ ...props.address })}
              </AddressHeader>
              <Link
                component="button"
                id="button-check-another"
                onClick={props.onCheckAnother}
              >
                Check another address
              </Link>
            </RowLayout>
          )}
          {getResultSubHeader()}
          {renderResult()}
        </ContentWrapper>
      </Container>
    </>
  );
}
